import { Link, Navigate, useLoaderData } from 'react-router-dom';
import BlogPost from '../components/blog/BlogPost';
import BlogGrid from '../components/blog/BlogGrid';
import ShareButtons from '@/components/ShareButtons';
import { getRelatedPosts } from '../lib/blog';
import type { BlogPost as BlogPostType } from '../lib/blog';

/**
 * Blog post detail page component
 *
 * Displays a single blog post with related posts.
 * Data is loaded via a route loader in routes.tsx so head tags are
 * available on the first SSR byte — crawlers and social previews see
 * the correct title, description, and og:image immediately.
 */
export default function BlogPostPage() {
  const { post, related } = useLoaderData() as { post: BlogPostType | null; related: BlogPostType[] };

  // Post not found - redirect to blog
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const relatedPosts = related ?? getRelatedPosts(post, 3);

  // SEO metadata + JSON-LD for this post are server-rendered in the route
  // head() (src/routes/blog.$slug.tsx) so crawlers and social/AI scrapers see
  // them on the first byte without executing JavaScript.
  const canonicalUrl = `https://digitallybaffled.com/blog/${post.slug}`;

  return (
    <div className="bg-background">
      {/* Back to Blog Link + Share */}
      <div className="max-w-4xl mx-auto px-4 pt-8 flex items-center justify-between gap-4 flex-wrap">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Blog
        </Link>
        <ShareButtons
          url={canonicalUrl}
          title={post.title}
          description={post.excerpt}
          size="sm"
          label=""
        />
      </div>

      {/* Blog Post Content */}
      <BlogPost post={post} />

      {/* Primary CTA — give readers a clear next step */}
      <div className="max-w-4xl mx-auto px-4 mt-16">
        <div className="rounded-3xl bg-primary px-8 py-10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Got a project in mind?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm md:text-base text-primary-foreground/85" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Book a free, no-obligation call. No jargon, no hard sell — just an honest chat about what your business needs. We'll reply within one working day.
          </p>
          <Link
            to="/get-started"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-background px-8 py-3.5 text-sm font-bold text-primary transition-all hover:opacity-90"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Book a free call
          </Link>
        </div>
      </div>


      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 py-16 border-t border-border mt-16">
          <h2 className="text-3xl font-bold text-foreground mb-8">
            Related Posts
          </h2>
          <BlogGrid posts={relatedPosts} variant="default" columns={3} />
        </div>
      )}
    </div>
  );
}
